// substance/fromLibraryItem.ts
// Adapts LibraryItem[] (from GET /me/inventory/library-items) into SubstanceRow[].
// One SubstanceRow per inventory lot; the caller's group-by-Code step reconstructs
// the CODE groups that SubstanceTable renders.
//
// Field-reconciliation notes (against inventory_types.ts as of 2026-06-15):
//   families[]:     { relId, familyId, displayText, color } — no slug.
//                   Family slug is derived by lowercasing displayText ("Fruity" → "fruity");
//                   if not a known FAMILY_COLORS key, defaults to "neutral".
//   customs[]:      { relId, customId, displayText } — displayText is non-optional.
//   location:       { relId, locationId, displayText } | null — field is displayText, not label.
//   identifiers[]:  { id, kind, value } — not populated by list controller yet (see B5).
//   chromatogram:   no field on InventoryRow list response — defaults to 0 (see below).

import type { LibraryItem, InventoryRow } from "@/services/inventory_types";
import { type SubstanceRow, type SubstanceType, type CompositionStatus, type SubstanceStock } from "./_model";

// ── helpers ──────────────────────────────────────────────────────────────────

function typeOf(inv: InventoryRow): SubstanceType {
  return inv.substanceNdbTable === "public.compound" ? "compound" : "mixture";
}

// Phase-B readiness heuristic (no server status field yet — see Deferred / Phase C):
//   compound                          → READY
//   mixture with chrom > 0 or formula → READY
//   mixture without either            → NOT_AVAILABLE
function statusOf(
  type: SubstanceType,
  chrom: number,
  hasFormula: boolean,
): CompositionStatus {
  if (type === "compound") return "READY";
  return chrom > 0 || hasFormula ? "READY" : "NOT_AVAILABLE";
}

function stockOf(inv: InventoryRow): SubstanceStock {
  const inStock = inv.outOfStockAt == null && inv.archivedAt == null;

  // sizeUnitId is the catalog id for the unit; sizeUnitRaw is the user-typed
  // display string. Prefer sizeUnitRaw (user-typed), then sizeUnitLabel (server-
  // resolved display text from inventory_type_unit). Never fall back to the raw UUID.
  const unitDisplay = inv.sizeUnitRaw ?? inv.sizeUnitLabel ?? "";
  const size = [inv.sizeValue, unitDisplay].filter(Boolean).join(" ").trim();

  // formId is catalog id; formRaw is display string. Prefer formRaw (user-typed),
  // then formLabel (server-resolved display text from inventory_type_form).
  const form = inv.formRaw ?? inv.formLabel ?? "";

  // location.displayText is the display label (real field name — NOT .label).
  const loc = inv.location?.displayText ?? "";

  // Standard: the drawer's Standard picker is SINGLE-SELECT by design, so we
  // hydrate the PRIMARY (first) standard only. Multi-standard lots are lossy
  // here — only standards[0] surfaces in the drawer (see report). Pass the
  // catalog id when present (drawer resolves id → name via toTypeValue + the
  // live standard catalog), else the display text (also resolves), NEVER the
  // standardRaw uuid-or-text fallback alone. Omit when there is no standard.
  const firstStandard = (inv.standards ?? [])[0];
  const standard = firstStandard
    ? firstStandard.standardId ?? firstStandard.displayText
    : undefined;

  // Claims: multi-select. Map each rel → { id } (catalogued claim) or
  // { raw: displayText } (typed claim). Never emit a bare uuid as a raw string.
  const claims = (inv.claims ?? []).map((c) =>
    c.claimId ? { id: c.claimId } : { raw: c.displayText },
  );

  // Currency: { id } when catalogued (the drawer resolves the id to a name via
  // the live currency catalog + toTypeValue), else { raw } from the user-typed
  // value, else omit. priceCurrencyLabel is available for read-only display but
  // the drawer needs the id/raw to re-bind the picker.
  const currency = inv.priceCurrencyId
    ? { id: inv.priceCurrencyId }
    : inv.priceCurrencyRaw
    ? { raw: inv.priceCurrencyRaw }
    : undefined;

  // Storage condition: prefer the catalog id (drawer resolves id → name via
  // toTypeValue + the live storage-condition catalog), else the raw display
  // string. Omit when neither is set.
  const storage = inv.storageConditionId ?? inv.storageConditionRaw ?? undefined;

  return {
    inStock,
    supplier: inv.supplierName ?? "",
    catalog: inv.supplierCatalogNo ?? "",
    lot: inv.lotNo ?? "",
    size,
    price: Number(inv.priceValue ?? 0),
    form,
    loc,
    expires: inv.expiresAt ? inv.expiresAt.slice(0, 10) : "",
    ...(standard !== undefined ? { standard } : {}),
    claims,
    ...(currency !== undefined ? { currency } : {}),
    ...(storage !== undefined ? { storage } : {}),
  };
}

// Extract identifier values by kind.
// identifiers[] is not populated by the current list controller (Phase B5 hydrates it);
// defaults to [] until then.
function idValues(inv: InventoryRow, kind: string): string[] {
  return (inv.identifiers ?? [])
    .filter((i) => i.kind === kind)
    .map((i) => i.value);
}

// Derive a family slug from the first attached family.
// InventoryRow.families[] has shape { relId, familyId, displayText, color } — no slug.
// The model's FAMILY_COLORS keys are the slugs ("fruity", "citrus", …).
// We lowercase the displayText and check against known keys; if absent, use "neutral".
function familySlugOf(inv: InventoryRow): string {
  // Lowercased displayText is the slug. Custom families keep their own slug
  // (no collapse to "neutral") — the row carries familyColor/familyLabel so it
  // still renders correctly even when the slug isn't in the fixed palette.
  const first = (inv.families ?? [])[0];
  return first ? first.displayText.toLowerCase() : "neutral";
}

// ── adapter ──────────────────────────────────────────────────────────────────

export function substanceRowsFromLibraryItems(items: LibraryItem[]): SubstanceRow[] {
  const rows: SubstanceRow[] = [];

  for (const li of items) {
    for (const inv of li.inventory) {
      const type = typeOf(inv);

      // Phase B: list endpoint doesn't expose chroma count yet (no chromatogramCount
      // field on InventoryRow). Defaults to 0; Task B5 can hydrate this server-side.
      const chrom = 0;

      const cas = idValues(inv, "cas");
      const ec = idValues(inv, "ec");
      const fema = idValues(inv, "fema");
      const internal = idValues(inv, "internal");
      // Custom-kind identifiers (anything beyond the 4 standard kinds) → extraIds.
      // Kind kept lowercase (server form) so the drawer's edit diff and the
      // persistence snapshot baseline match exactly (zero spurious add/remove).
      const extraIds = (inv.identifiers ?? [])
        .filter((i) => !["cas", "ec", "fema", "internal"].includes(i.kind))
        .map((i) => ({ kind: i.kind, value: i.value }));

      // customs[].displayText is non-optional on the real type.
      const tags = (inv.customs ?? []).map((c) => c.displayText).filter(Boolean);

      // added/lastUsed: InventoryRow has camelCase createdAt/updatedAt (string).
      const added = inv.createdAt.slice(0, 10);
      const lastUsed = inv.updatedAt ? inv.updatedAt.slice(0, 10) : null;

      rows.push({
        id: inv.id,
        name: li.name,
        code: li.code,
        type,
        family: familySlugOf(inv),
        familyColor: (inv.families ?? [])[0]?.color ?? null,
        familyLabel: (inv.families ?? [])[0]?.displayText,
        cas,
        ec,
        fema,
        internal,
        extraIds,
        // target/formulation are standalone library entries deferred to Phase C.
        usage: ["ingredient"],
        status: statusOf(type, chrom, false /* formula: local-only in Phase B */),
        added,
        lastUsed,
        tags,
        chrom,
        // formula: standalone per-substance formulation persistence is deferred to Phase C.
        formula: null,
        stock: stockOf(inv),
      });
    }
  }

  return rows;
}
